import type { FormInstance } from 'antd';
import type { ApiError } from '../api/client';

/**
 * Splits a server-reported field path ("cityName", "bankAccounts[0].iban")
 * into the array form AntD's `Form.Item name` / `form.setFields` understand
 * (`['bankAccounts', 0, 'iban']`) — the API's `AllExceptionsFilter` emits
 * exactly this dot/bracket convention (see `formatZodPath` on the API side),
 * so this is its inverse.
 */
function pathToNamePath(path: string): (string | number)[] {
  const parts: (string | number)[] = [];
  const segmentPattern = /([^.[\]]+)|\[(\d+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = segmentPattern.exec(path)) !== null) {
    parts.push(match[2] !== undefined ? Number(match[2]) : match[1]);
  }
  return parts;
}

/**
 * Maps a server error's field-level `details` onto form fields via
 * `form.setFields` — the one common helper every CRUD drawer's mutation
 * `onError` goes through (task brief's UI convention). Works for any error
 * that carries `details` (400 VALIDATION, but also e.g. a 409 DUPLICATE from
 * a unique-constraint violation — see AllExceptionsFilter's P2002 mapping —
 * which is exactly as field-scoped and deserves the same treatment).
 *
 * Returns `true` when at least one field error was applied; callers should
 * fall back to `message.error(apiError.message)` when it returns `false`
 * (no `details`, i.e. a plain toast-worthy error like 409 REFERENCED).
 */
export function applyServerErrors(form: FormInstance, apiError: ApiError | null | undefined): boolean {
  if (!apiError?.details || apiError.details.length === 0) return false;
  form.setFields(
    apiError.details.map((detail) => ({
      name: pathToNamePath(detail.field),
      errors: [detail.message],
    })),
  );
  return true;
}

/**
 * Empty/whitespace-only input -> `undefined`, so an optional zod field (e.g.
 * `z.email().optional()`) never sees a `''` it would reject as an invalid
 * value in its own right. An AntD field the user never touched is already
 * `undefined`; one they focused and cleared comes back as `''`.
 */
export function undefinedIfEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/** Same normalization `cuiField` applies server-side (firm.schema.ts /
 * client.schema.ts): strip an optional leading "RO", uppercase, drop
 * whitespace — so the client-side `isValidCui` check runs against the exact
 * string the server will validate, whatever prefix/casing the user typed. */
export function normalizeCui(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase().replace(/^RO/, '').replace(/\s+/g, '');
}

/** Same normalization the server applies to IBANs before checksum
 * validation: uppercase, drop whitespace. */
export function normalizeIban(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase().replace(/\s+/g, '');
}
